/**
 * Client-side image compression utility
 * Uses native Canvas API for the main path.
 *
 * Designed for grass identification uploads where:
 * - AI analysis works well with 1-2MB images
 * - Max dimension of 1920px is more than sufficient
 * - JPEG output balances quality and file size
 *
 * HEIC/HEIF (iPhone default) is decoded via heic2any, lazy-imported only when
 * a HEIC file is detected so the libheif bundle stays out of the main JS.
 */

import { isHeicFile, isHeicByMagicBytes, convertHeicToJpeg } from "./heic-to-jpeg"

interface CompressionOptions {
  maxSizeMB?: number           // Target max file size in MB (default: 2)
  maxWidthOrHeight?: number    // Max dimension in pixels (default: 1920)
  quality?: number             // Initial JPEG quality 0-1 (default: 0.85)
  minQuality?: number          // Minimum quality before aggressive resize (default: 0.5)
  maxMegapixels?: number       // Reject extreme decoded images above this limit (default: 50)
}

interface CompressionResult {
  file: File
  originalSize: number
  compressedSize: number
  wasCompressed: boolean
  compressionRatio?: number    // e.g., 0.25 means compressed to 25% of original
  finalQuality?: number        // The quality setting that was used
  finalDimensions?: { width: number; height: number }
}

/**
 * A decoded image normalized across the two decode paths, so the caller can draw
 * it to a canvas without caring which path produced it.
 */
export interface DecodedImage {
  source: CanvasImageSource
  width: number
  height: number
  cleanup: () => void
}

/**
 * Decode an image file to something canvas can draw, trying two paths:
 *
 *   1. createImageBitmap — fast, decodes off the main thread. But it is stricter
 *      than the `<img>` decoder and, on Android Chrome especially, throws on
 *      large high-megapixel photos (modern phones shoot 50-108MP) and a few odd
 *      formats. That was the dominant cause of INVALID_IMAGE in the field
 *      (PostHog: 100% Android Chrome).
 *   2. HTMLImageElement decode — the browser's native `<img>` path. It tolerates
 *      large images and formats createImageBitmap rejects, so it recovers the
 *      uploads that previously failed outright. Within this path we try the
 *      strict `img.decode()` first, then fall back to the `load` event, because
 *      Android Chrome's `decode()` rejects very large photos (it forces a full
 *      rasterization) that the `load` event accepts just fine. That extra
 *      fallback is what recovers the remaining Android INVALID_IMAGE cases:
 *      both `createImageBitmap` AND `decode()` reject the photo, but `load`
 *      still surfaces usable dimensions to draw from.
 *
 * Throws only if ALL paths fail (genuinely corrupt / unsupported).
 */
export async function decodeImage(
  file: File,
  options?: {
    /**
     * Aborting revokes the fallback path's object URL even while the decode is
     * still pending. Without it, a decode that never settles (hung `<img>`)
     * strands the URL forever, because the caller's cleanup only runs after
     * this promise resolves. Revocation is idempotent, so a later cleanup()
     * or error path cannot double-revoke.
     */
    signal?: AbortSignal
  },
): Promise<DecodedImage> {
  try {
    const bitmap = await createImageBitmap(file)
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      cleanup: () => bitmap.close(),
    }
  } catch {
    // Fall through to the <img> path below.
  }

  const url = URL.createObjectURL(file)
  let revoked = false
  const revokeUrl = () => {
    if (revoked) return
    revoked = true
    URL.revokeObjectURL(url)
  }
  const signal = options?.signal
  if (signal?.aborted) revokeUrl()
  else signal?.addEventListener('abort', revokeUrl, { once: true })
  try {
    const img = new Image()
    img.decoding = 'async'

    // Resolve via the `load` event (and reject via `error`). We attach these
    // before setting `src` so we never miss a synchronously-fired event, and we
    // use them both as the primary path (when `decode()` is unavailable) and as
    // the fallback when `decode()` rejects a large image.
    const loadPromise = new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('Image element failed to load'))
    })
    img.src = url

    if (typeof img.decode === 'function') {
      try {
        await img.decode()
      } catch {
        // Android Chrome's decode() rejects large high-MP photos that the load
        // event handles fine. Wait for load before giving up. If the image is
        // already loaded by now this resolves immediately; if it is genuinely
        // undecodable the error handler rejects.
        await loadPromise
      }
    } else {
      await loadPromise
    }

    const width = img.naturalWidth || img.width
    const height = img.naturalHeight || img.height
    if (!width || !height) {
      throw new Error('Decoded image has zero dimensions')
    }
    return {
      source: img,
      width,
      height,
      cleanup: revokeUrl,
    }
  } catch (err) {
    revokeUrl()
    throw err
  }
}

/**
 * Compresses an image file if it exceeds the size threshold
 *
 * Strategy:
 * 1. Decode every file to validate dimensions and the megapixel safety ceiling
 * 2. Return as-is only when both byte and dimension thresholds are satisfied
 * 3. Resize to max dimensions if needed
 * 4. Progressively reduce quality until target size is met
 * 5. If still too large at min quality, reduce dimensions further
 *
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise with the compressed file and metadata
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const {
    maxSizeMB = 2,
    maxWidthOrHeight = 1920,
    quality = 0.85,
    minQuality = 0.5,
    // Preserve common 48 MP phone photos, but reject higher full-resolution
    // modes before allocating another large canvas. The browser must decode to
    // inspect dimensions; bounding this at 50 MP avoids a 108 MP mobile-tab OOM.
    maxMegapixels = 50,
  } = options

  const maxSizeBytes = maxSizeMB * 1024 * 1024
  const originalSize = file.size

  // HEIC/HEIF: browsers can't decode these via createImageBitmap (Safari aside),
  // so convert to JPEG up front. The conversion result is what we compress
  // below, and originalSize stays as the user-picked file's size for accurate
  // ratio reporting.
  let workingFile = file
  if (isHeicFile(file)) {
    try {
      workingFile = await convertHeicToJpeg(file)
    } catch (error) {
      throw new ImageCompressionError(
        'We could not read this iPhone photo. Try saving it as JPEG and uploading again.',
        'HEIC_CONVERSION_FAILED'
      )
    }
  }

  let decoded: DecodedImage

  try {
    // Decode via createImageBitmap, falling back to an <img> element for the
    // large/edge-case photos createImageBitmap rejects (see decodeImage).
    decoded = await decodeImage(workingFile)
  } catch (error) {
    // Both decode paths failed. HEIC is the prime suspect: browsers can't decode
    // it (Safari aside), and Android photo pickers often hand over an iOS-origin
    // HEIC with an empty MIME type and a generic name, so isHeicFile() missed it.
    // Sniff the magic bytes and retry once via the HEIC converter before giving
    // up.
    if (workingFile === file && (await isHeicByMagicBytes(workingFile))) {
      try {
        workingFile = await convertHeicToJpeg(workingFile)
        decoded = await decodeImage(workingFile)
      } catch {
        throw new ImageCompressionError(
          'We could not read this iPhone photo. Try saving it as JPEG and uploading again.',
          'HEIC_CONVERSION_FAILED'
        )
      }
    } else {
      // File is not a valid image or is corrupted
      throw new ImageCompressionError(
        'Unable to process image. The file may be corrupted or in an unsupported format.',
        'INVALID_IMAGE'
      )
    }
  }

  try {
    const { width: originalWidth, height: originalHeight } = decoded
    const megapixels = (originalWidth * originalHeight) / 1_000_000

    if (!Number.isFinite(megapixels) || megapixels > maxMegapixels) {
      throw new ImageCompressionError(
        `This photo is ${Number.isFinite(megapixels) ? megapixels.toFixed(1) : 'too many'} megapixels. Please use one under ${maxMegapixels} megapixels.`,
        'MEGAPIXEL_LIMIT_EXCEEDED',
      )
    }

    // A byte-only bypass is not a safe fidelity bound: modern phone JPEGs can
    // be only a few megabytes while still decoding to 48-108 MP. Probe every
    // accepted image first, then bypass re-encoding only when BOTH byte and
    // dimension limits are satisfied. Returning dimensions for this path keeps
    // upload/canary telemetry complete.
    if (
      workingFile.size <= maxSizeBytes &&
      originalWidth <= maxWidthOrHeight &&
      originalHeight <= maxWidthOrHeight
    ) {
      return {
        file: workingFile,
        originalSize,
        compressedSize: workingFile.size,
        wasCompressed: workingFile !== file,
        finalDimensions: { width: originalWidth, height: originalHeight },
      }
    }

    // Calculate initial dimensions
    let targetWidth = originalWidth
    let targetHeight = originalHeight
    let currentMaxDimension = maxWidthOrHeight

    // Resize if needed
    if (originalWidth > currentMaxDimension || originalHeight > currentMaxDimension) {
      const dimensions = calculateDimensions(originalWidth, originalHeight, currentMaxDimension)
      targetWidth = dimensions.width
      targetHeight = dimensions.height
    }

    // Create canvas
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new ImageCompressionError(
        'Unable to process image. Please try a different browser.',
        'CANVAS_ERROR'
      )
    }

    // Try compression with progressively lower quality
    let blob: Blob | null = null
    let currentQuality = quality
    let attempts = 0
    const maxAttempts = 10 // Prevent infinite loops

    while (attempts < maxAttempts) {
      attempts++

      // Set canvas size
      canvas.width = targetWidth
      canvas.height = targetHeight

      // Draw with high-quality smoothing
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      // Fill with white background (handles PNG transparency gracefully)
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, targetWidth, targetHeight)

      // Draw the image
      ctx.drawImage(decoded.source, 0, 0, targetWidth, targetHeight)

      // Convert to blob
      blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', currentQuality)
      })

      if (blob && blob.size <= maxSizeBytes) {
        // Success! We're under the target size
        break
      }

      // Still too large - try reducing quality first
      if (currentQuality > minQuality) {
        currentQuality = Math.max(minQuality, currentQuality - 0.1)
      } else {
        // Quality is at minimum, reduce dimensions instead
        currentMaxDimension = Math.round(currentMaxDimension * 0.75)

        if (currentMaxDimension < 400) {
          // Don't go below 400px - image would be too small for analysis
          break
        }

        // Recalculate dimensions
        const newDimensions = calculateDimensions(originalWidth, originalHeight, currentMaxDimension)
        targetWidth = newDimensions.width
        targetHeight = newDimensions.height

        // Reset quality for new dimension attempt
        currentQuality = quality
      }
    }

    if (!blob) {
      throw new ImageCompressionError(
        'Failed to compress image. Please try a smaller image.',
        'COMPRESSION_FAILED'
      )
    }

    // The size target is a postcondition, not a best-effort hint. Returning an
    // oversized final iteration merely moves the failure to the 10 MB upload
    // route (or a platform body limit) and makes the active fidelity variant
    // unpredictable.
    if (blob.size > maxSizeBytes) {
      throw new ImageCompressionError(
        'We could not prepare this photo within the upload limit. Please try a smaller image.',
        'OUTPUT_LIMIT_UNMET',
      )
    }

    // Create new file with original name (but .jpg extension)
    // Handle files with no extension by appending .jpg
    const hasExtension = /\.[^.]+$/.test(workingFile.name)
    const fileName = hasExtension
      ? workingFile.name.replace(/\.[^.]+$/, '.jpg')
      : `${workingFile.name}.jpg`
    const compressedFile = new File([blob], fileName, {
      type: 'image/jpeg',
      lastModified: Date.now()
    })

    return {
      file: compressedFile,
      originalSize,
      compressedSize: compressedFile.size,
      wasCompressed: true,
      compressionRatio: compressedFile.size / originalSize,
      finalQuality: currentQuality,
      finalDimensions: { width: targetWidth, height: targetHeight }
    }
  } finally {
    // Release the decoded image (ImageBitmap.close() or object-URL revoke).
    decoded.cleanup()
  }
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  width: number,
  height: number,
  maxDimension: number
): { width: number; height: number } {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height }
  }

  if (width > height) {
    return {
      width: maxDimension,
      height: Math.round((height / width) * maxDimension)
    }
  } else {
    return {
      width: Math.round((width / height) * maxDimension),
      height: maxDimension
    }
  }
}

/**
 * Custom error class for image compression errors
 */
export class ImageCompressionError extends Error {
  code: string

  constructor(message: string, code: string) {
    super(message)
    this.name = 'ImageCompressionError'
    this.code = code
  }
}

/**
 * Formats file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Check if the browser supports image compression
 */
export function isCompressionSupported(): boolean {
  return (
    typeof createImageBitmap === 'function' &&
    typeof document !== 'undefined' &&
    typeof document.createElement === 'function'
  )
}

/**
 * Strip EXIF/metadata from an image file by redrawing to canvas
 *
 * This removes potentially sensitive data like:
 * - GPS coordinates
 * - Camera/device info
 * - Timestamps
 * - Thumbnail previews
 *
 * @param file - The image file to process
 * @param quality - JPEG quality for output (default: 0.92 for minimal quality loss)
 * @returns Promise with the cleaned file
 */
export async function stripExifData(
  file: File,
  quality: number = 0.92
): Promise<File> {
  if (!isCompressionSupported()) {
    // Can't process - return original (shouldn't happen in modern browsers)
    return file
  }

  let decoded: DecodedImage

  try {
    // Same two-path decode as compressImage: createImageBitmap rejects some
    // large/edge-case photos (Android Chrome especially) that the <img>
    // fallback handles fine.
    decoded = await decodeImage(file)
  } catch {
    // Invalid image - return as-is
    return file
  }

  try {
    const { width, height } = decoded

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      return file
    }

    canvas.width = width
    canvas.height = height

    // Draw with high quality
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Fill background (handles transparency)
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, width, height)

    // Draw the image (this strips all EXIF data)
    ctx.drawImage(decoded.source, 0, 0, width, height)

    // Convert to blob
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', quality)
    })

    if (!blob) {
      return file
    }

    // Create new file
    const hasExtension = /\.[^.]+$/.test(file.name)
    const fileName = hasExtension
      ? file.name.replace(/\.[^.]+$/, '.jpg')
      : `${file.name}.jpg`

    return new File([blob], fileName, {
      type: 'image/jpeg',
      lastModified: Date.now()
    })
  } finally {
    decoded.cleanup()
  }
}
